import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, MapPin, Cloud, ChevronRight, Calendar, Trophy, Sparkles, AlertTriangle, Medal, Users, Crown } from 'lucide-react';
import axios from 'axios';
import { format, parseISO, differenceInSeconds } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import { getToken } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Region code to name mapping
const REGION_NAMES = {
    'UK': 'UK',
    'IRE': 'Ireland',
    'FR': 'France',
    'USA': 'USA',
    'ZA': 'South Africa',
    'AUS': 'Australia'
};

// Helper to format selected regions as readable string
const formatRegionsList = (regions) => {
    if (!regions || regions.length === 0) return 'UK';
    const names = regions.map(r => REGION_NAMES[r] || r);
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
};

// Helper to format going condition
const formatGoing = (going) => {
    if (!going || going === 'Unknown') return null;
    return going
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
        .replace('To ', 'to ');
};

const CountdownTimer = ({ targetTime }) => {
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    useEffect(() => {
        const target = parseISO(targetTime);
        
        const updateTimer = () => {
            const now = new Date();
            const diff = differenceInSeconds(target, now);
            
            if (diff <= 0) {
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }
            
            const days = Math.floor(diff / 86400);
            const hours = Math.floor((diff % 86400) / 3600);
            const minutes = Math.floor((diff % 3600) / 60);
            const seconds = diff % 60;
            
            setTimeLeft({ days, hours, minutes, seconds });
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [targetTime]);

    // Show days format if more than 24 hours, otherwise just hours
    const showDays = timeLeft.days > 0;

    return (
        <div className="flex gap-3">
            {showDays ? (
                // Days, Hours, Minutes, Seconds format
                [
                    { value: timeLeft.days, label: 'DAYS' },
                    { value: timeLeft.hours, label: 'HRS' },
                    { value: timeLeft.minutes, label: 'MIN' },
                    { value: timeLeft.seconds, label: 'SEC' },
                ].map((item, idx) => (
                    <div key={idx} className="text-center">
                        <div className="countdown-digit">{String(item.value).padStart(2, '0')}</div>
                        <div className="text-xs text-muted-foreground font-medium">{item.label}</div>
                    </div>
                ))
            ) : (
                // Hours, Minutes, Seconds format
                [
                    { value: timeLeft.hours, label: 'HRS' },
                    { value: timeLeft.minutes, label: 'MIN' },
                    { value: timeLeft.seconds, label: 'SEC' },
                ].map((item, idx) => (
                    <div key={idx} className="text-center">
                        <div className="countdown-digit">{String(item.value).padStart(2, '0')}</div>
                        <div className="text-xs text-muted-foreground font-medium">{item.label}</div>
                    </div>
                ))
            )}
        </div>
    );
};

const MeetingCard = ({ meeting, isAbandoned }) => {
    const going = formatGoing(meeting.meta?.['stage:going']?.value);
    const weather = meeting.meta?.['stage:weather']?.value;
    const raceCount = meeting.events?.length || 0;
    const firstRace = meeting.events?.[0];

    return (
        <Link to={`/meeting/${meeting.id}`} data-testid={`meeting-card-${meeting.id}`}>
            <Card className={`card-hover cursor-pointer h-full ${isAbandoned ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg font-heading">{meeting.name}</CardTitle>
                        <div className="flex items-center gap-2">
                            {isAbandoned ? (
                                <Badge variant="destructive" className="font-semibold">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    Abandoned
                                </Badge>
                            ) : (
                                <Badge variant="secondary" className="font-data">
                                    {raceCount} races
                                </Badge>
                            )}
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-3">
                    {isAbandoned ? (
                        <p className="text-sm text-muted-foreground">
                            This meeting has been abandoned due to adverse conditions.
                        </p>
                    ) : (
                        <>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center gap-1">
                                    <Clock className="h-4 w-4" />
                                    {firstRace && format(parseISO(firstRace.start), 'HH:mm')}
                                </div>
                                {going && (
                                    <div className="flex items-center gap-1">
                                        <MapPin className="h-4 w-4" />
                                        {going}
                                    </div>
                                )}
                                {weather && (
                                    <div className="flex items-center gap-1">
                                        <Cloud className="h-4 w-4" />
                                        {weather}
                                    </div>
                                )}
                            </div>
                            
                            {/* Race times */}
                            <div className="flex flex-wrap gap-2">
                                {meeting.events?.slice(0, 5).map(event => (
                                    <Badge 
                                        key={event.id} 
                                        variant="outline" 
                                        className="font-data"
                                    >
                                        {format(parseISO(event.start), 'HH:mm')}
                                    </Badge>
                                ))}
                                {meeting.events?.length > 5 && (
                                    <Badge variant="outline" className="font-data">
                                        +{meeting.events.length - 5}
                                    </Badge>
                                )}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </Link>
    );
};

export default function HomePage() {
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nextRace, setNextRace] = useState(null);
    const [selectedRegions, setSelectedRegions] = useState(['UK']);
    const [userCompetition, setUserCompetition] = useState(null);
    const { isAuthenticated } = useAuth();

    // Load region preferences
    useEffect(() => {
        const loadRegions = async () => {
            // First check localStorage
            const storedRegions = localStorage.getItem('racewise_regions');
            if (storedRegions) {
                try {
                    setSelectedRegions(JSON.parse(storedRegions));
                } catch (e) {
                    console.error('Failed to parse stored regions');
                }
            }
            
            // If logged in, fetch from API (overwrites localStorage)
            if (isAuthenticated) {
                try {
                    const token = getToken();
                    const response = await axios.get(`${API}/settings`, {
                        headers: token ? { Authorization: `Bearer ${token}` } : {}
                    });
                    if (response.data.regions && response.data.regions.length > 0) {
                        setSelectedRegions(response.data.regions);
                        localStorage.setItem('racewise_regions', JSON.stringify(response.data.regions));
                    }
                } catch (error) {
                    console.error('Failed to fetch region settings:', error);
                }
            }
        };
        loadRegions();
    }, [isAuthenticated]);

    // Fetch user's active competitions
    useEffect(() => {
        const fetchUserCompetitions = async () => {
            if (!isAuthenticated) return;
            
            try {
                const token = getToken();
                const response = await axios.get(`${API}/competitions`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const competitions = response.data.competitions || [];
                
                // Filter out completed competitions - only show active/upcoming
                const activeCompetitions = competitions.filter(c => c.status !== 'complete' && c.status !== 'completed');
                
                // Find featured competition the user has joined (that's still active)
                const featuredJoined = activeCompetitions.find(c => c.is_featured && c.is_joined);
                // Or any active competition they've joined
                const anyJoined = activeCompetitions.find(c => c.is_joined);
                const comp = featuredJoined || anyJoined;
                
                if (comp) {
                    // Fetch full competition details to get races and picks
                    try {
                        const detailRes = await axios.get(`${API}/competitions/${comp.id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        // Handle both wrapped and unwrapped responses
                        const fullComp = detailRes.data.competition || detailRes.data;
                        
                        // Double-check if all races are finished (competition might be complete)
                        const allRacesFinished = (fullComp.races || []).length > 0 && 
                            (fullComp.races || []).every(r => r.status === 'finished');
                        
                        if (allRacesFinished) {
                            // Competition is actually complete, don't show it
                            setUserCompetition(null);
                            return;
                        }
                        
                        // Calculate picks made
                        const picksMade = Object.keys(fullComp.my_picks || {}).length;
                        const totalRaces = fullComp.races?.length || 0;
                        
                        // Find next upcoming race in the competition
                        const now = new Date();
                        const upcomingRaces = (fullComp.races || [])
                            .filter(r => new Date(r.race_time) > now && r.status !== 'finished')
                            .sort((a, b) => new Date(a.race_time) - new Date(b.race_time));
                        
                        setUserCompetition({
                            ...comp,
                            picks_made: picksMade,
                            total_races: totalRaces,
                            next_race: upcomingRaces[0] || null,
                            has_picks: picksMade > 0
                        });
                    } catch (err) {
                        console.error('Failed to fetch competition details:', err);
                        setUserCompetition(comp);
                    }
                } else {
                    setUserCompetition(null);
                }
            } catch (error) {
                console.error('Failed to fetch competitions:', error);
            }
        };
        fetchUserCompetitions();
    }, [isAuthenticated]);

    useEffect(() => {
        const fetchMeetings = async () => {
            setLoading(true);
            try {
                // Fetch meetings for all selected regions
                const allMeetings = [];
                
                for (const region of selectedRegions) {
                    const response = await axios.get(`${API}/racing/stages`, {
                        params: { country: region }
                    });
                    const items = response.data.items || [];
                    // Add region to each meeting for display
                    items.forEach(m => {
                        m.region = region;
                        allMeetings.push(m);
                    });
                }
                
                // Detect abandoned meetings - check stage status and event statuses
                const isAbandoned = (meeting) => {
                    // First check if backend already flagged it as abandoned (stage-level status)
                    if (meeting.isAbandoned) return true;
                    
                    // Check stage status
                    const stageStatus = (meeting.status || '').toUpperCase();
                    if (stageStatus.includes('ABANDONED')) return true;
                    
                    // Check if ALL events are abandoned
                    const events = meeting.events || [];
                    if (events.length === 0) return false;
                    
                    const abandonedStatuses = ['ABANDONED', 'EVENT_ABANDONED', 'MEETING_ABANDONED'];
                    const allAbandoned = events.every(e => 
                        abandonedStatuses.some(s => (e.status || '').toUpperCase().includes(s))
                    );
                    
                    return allAbandoned;
                };
                
                // Sort meetings: active first, then by first race time
                const sortedMeetings = [...allMeetings].sort((a, b) => {
                    const aAbandoned = isAbandoned(a);
                    const bAbandoned = isAbandoned(b);
                    // Active meetings come first
                    if (aAbandoned && !bAbandoned) return 1;
                    if (!aAbandoned && bAbandoned) return -1;
                    // Then sort by first race time
                    const aTime = a.events?.[0]?.start || '9999';
                    const bTime = b.events?.[0]?.start || '9999';
                    return aTime.localeCompare(bTime);
                });
                
                // Mark each meeting with abandoned status
                const meetingsWithStatus = sortedMeetings.map(m => ({
                    ...m,
                    isAbandoned: isAbandoned(m)
                }));
                setMeetings(meetingsWithStatus);
                
                // Find next race - ONLY from active (non-abandoned) meetings
                const now = new Date();
                let nearest = null;
                const activeMeetings = allMeetings.filter(m => !isAbandoned(m));
                activeMeetings.forEach(meeting => {
                    meeting.events?.forEach(event => {
                        const eventTime = parseISO(event.start);
                        if (eventTime > now && (!nearest || eventTime < parseISO(nearest.start))) {
                            nearest = { ...event, venue: meeting.name };
                        }
                    });
                });
                setNextRace(nearest);
            } catch (error) {
                console.error('Failed to fetch meetings:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchMeetings();
    }, [selectedRegions]);

    return (
        <div className="min-h-screen pb-20 md:pb-8">
            {/* Hero Section */}
            <section className="relative overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center opacity-20 dark:opacity-10"
                    style={{ backgroundImage: 'url(https://images.pexels.com/photos/14096925/pexels-photo-14096925.jpeg)' }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-background/50 to-background" />
                
                <div className="container-app relative py-16 md:py-24">
                    <div className="max-w-2xl">
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 uppercase page-title">
                            Podium Tips Meet{' '}
                            <span className="text-gradient">AI Analysis</span>
                        </h1>
                        <p className="text-lg text-muted-foreground mb-8">
                            Compare Podium picks with AI predictions. Track performance, find winners, and make smarter bets.
                        </p>
                        
                        <div className="flex flex-wrap gap-4">
                            <Link to="/races">
                                <Button size="lg" className="btn-pill gap-2" data-testid="view-races-btn">
                                    <Calendar className="h-5 w-5" />
                                    View Today&apos;s Races
                                </Button>
                            </Link>
                            {!isAuthenticated && (
                                <Link to="/register">
                                    <Button size="lg" variant="outline" className="btn-pill" data-testid="get-started-btn">
                                        Get Started Free
                                    </Button>
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Tipping Comps Section - Show if user has joined a competition */}
            {userCompetition && (
                <section className="container-app -mt-8 relative z-20 mb-4">
                    <div className="flex items-center gap-2 mb-4">
                        <Trophy className="h-5 w-5 text-amber-500" />
                        <h2 className="font-heading text-lg font-bold">Tipping Comps</h2>
                    </div>
                    <Card className="border-2 border-amber-500/50 bg-gradient-to-r from-amber-500/10 via-primary/5 to-amber-500/10 overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
                        <CardContent className="p-6 relative">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                                <div className="flex items-start gap-4">
                                    <div className="w-14 h-14 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                                        <Trophy className="h-7 w-7 text-amber-500" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            {userCompetition.is_featured && (
                                                <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30">
                                                    <Crown className="h-3 w-3 mr-1" />
                                                    Featured
                                                </Badge>
                                            )}
                                            <Badge variant="outline" className="text-green-500 border-green-500/30">
                                                <Medal className="h-3 w-3 mr-1" />
                                                You&apos;re In!
                                            </Badge>
                                            {userCompetition.has_picks && (
                                                <Badge variant="outline" className="text-primary border-primary/30">
                                                    {userCompetition.picks_made}/{userCompetition.total_races} picks
                                                </Badge>
                                            )}
                                        </div>
                                        <h3 className="font-heading text-xl font-bold mb-1">{userCompetition.name}</h3>
                                        <p className="text-muted-foreground text-sm">{userCompetition.meeting_name}</p>
                                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                                            <span className="flex items-center gap-1">
                                                <Users className="h-4 w-4" />
                                                {userCompetition.entry_count} competitors
                                            </span>
                                            <span>
                                                {new Date(userCompetition.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {new Date(userCompetition.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="flex flex-col items-end gap-3">
                                    {/* Next Race in Competition Timer */}
                                    {userCompetition.next_race && (
                                        <div className="text-right">
                                            <div className="flex items-center gap-2 text-amber-500 mb-1">
                                                <Clock className="h-4 w-4" />
                                                <span className="text-xs font-semibold uppercase tracking-wide">Next Race</span>
                                            </div>
                                            <p className="text-sm font-medium truncate max-w-[200px]" title={userCompetition.next_race.event_title}>
                                                {userCompetition.next_race.event_title?.slice(0, 25)}{userCompetition.next_race.event_title?.length > 25 ? '...' : ''}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {format(parseISO(userCompetition.next_race.race_time), 'HH:mm')} - {userCompetition.next_race.stage_name}
                                            </p>
                                            <div className="mt-2">
                                                <CountdownTimer targetTime={userCompetition.next_race.race_time} />
                                            </div>
                                        </div>
                                    )}
                                    
                                    <Link to={`/competitions/${userCompetition.id}`}>
                                        {(() => {
                                            // Check if next race is more than 48 hours away
                                            const isMoreThan48Hours = userCompetition.next_race && 
                                                differenceInSeconds(parseISO(userCompetition.next_race.race_time), new Date()) > 48 * 60 * 60;
                                            
                                            if (isMoreThan48Hours && !userCompetition.has_picks) {
                                                return (
                                                    <Button size="lg" className="btn-pill gap-2 bg-gray-500 hover:bg-gray-600 text-white cursor-not-allowed" disabled data-testid="coming-soon-btn">
                                                        <Clock className="h-5 w-5" />
                                                        Coming Soon
                                                    </Button>
                                                );
                                            }
                                            
                                            return (
                                                <Button size="lg" className="btn-pill gap-2 bg-amber-500 hover:bg-amber-600 text-black" data-testid="view-competition-btn">
                                                    <Trophy className="h-5 w-5" />
                                                    {userCompetition.has_picks ? 'View Competition' : 'Make Your Picks'}
                                                    <ChevronRight className="h-4 w-4" />
                                                </Button>
                                            );
                                        })()}
                                    </Link>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </section>
            )}

            {/* Today's Meetings */}
            <section className="container-app py-12">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="font-heading text-2xl font-bold">Today&apos;s Meetings</h2>
                        <p className="text-muted-foreground">
                            Browse racecards from {formatRegionsList(selectedRegions)}
                        </p>
                    </div>
                    <Link to="/races">
                        <Button variant="ghost" className="gap-1" data-testid="view-all-races">
                            View All
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </Link>
                </div>

                {/* Next Race Countdown - Now inside Today's Meetings */}
                {nextRace && (
                    <Card className="ai-insight-card border-primary/30 mb-6">
                        <CardContent className="p-6">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                                <div>
                                    <div className="flex items-center gap-2 text-primary mb-2">
                                        <Sparkles className="h-5 w-5" />
                                        <span className="text-sm font-semibold uppercase tracking-wide">Next Race</span>
                                    </div>
                                    <h3 className="font-heading text-xl font-semibold mb-1">{nextRace.title}</h3>
                                    <p className="text-muted-foreground">{nextRace.venue}</p>
                                </div>
                                
                                <div className="flex items-center gap-6">
                                    <CountdownTimer targetTime={nextRace.start} />
                                    <Link to={`/race/${nextRace.id}`} state={{ courseName: nextRace.venue }}>
                                        <Button className="btn-pill gap-2" data-testid="view-race-btn">
                                            View Race
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <Card key={i}>
                                <CardHeader>
                                    <Skeleton className="h-6 w-32" />
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <Skeleton className="h-4 w-full" />
                                    <div className="flex gap-2">
                                        <Skeleton className="h-6 w-16" />
                                        <Skeleton className="h-6 w-16" />
                                        <Skeleton className="h-6 w-16" />
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : meetings.length === 0 ? (
                    <Card className="text-center py-12">
                        <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No meetings scheduled for today</p>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {meetings.map((meeting, idx) => (
                            <div key={meeting.id} className="animate-fade-in" style={{ animationDelay: `${idx * 100}ms` }}>
                                <MeetingCard meeting={meeting} isAbandoned={meeting.isAbandoned} />
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Features Section */}
            <section className="container-app py-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        {
                            icon: Sparkles,
                            title: 'AI Race Analysis',
                            description: 'Get plain-English breakdowns of every race, powered by AI. No jargon, just insights.'
                        },
                        {
                            icon: Trophy,
                            title: 'Track Your Favourites',
                            description: 'Build your stable of horses, jockeys and trainers. Get notified when they run.'
                        },
                        {
                            icon: Calendar,
                            title: 'Full Racecards',
                            description: 'Comprehensive race information with form, odds and stats - all in one place.'
                        }
                    ].map((feature, idx) => (
                        <Card key={idx} className="text-center p-6">
                            <feature.icon className="h-10 w-10 mx-auto text-primary mb-4" />
                            <h3 className="font-heading font-semibold text-lg mb-2">{feature.title}</h3>
                            <p className="text-sm text-muted-foreground">{feature.description}</p>
                        </Card>
                    ))}
                </div>
            </section>

            {/* Responsible Gambling Banner */}
            <section className="container-app py-8">
                <Card className="bg-muted/50 border-none">
                    <CardContent className="p-6 text-center">
                        <p className="text-sm text-muted-foreground">
                            <strong>Gamble Responsibly.</strong> 18+ only. This is a demo platform - no real money involved.
                            For support, visit{' '}
                            <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" className="underline">
                                BeGambleAware.org
                            </a>
                        </p>
                    </CardContent>
                </Card>
            </section>
        </div>
    );
}
